// Top-level component. Defines every route the SPA exposes:
//
//   /                                       — Marketing landing page (public)
//   /login-bridge                           — JWT handshake from WordPress (public).
//                                             This is the CANONICAL handshake path —
//                                             it matches the WP `functions.php`
//                                             running on ki-wealth.com which
//                                             redirects every authenticated user to
//                                             https://<host>/login-bridge?token=<JWT>.
//   /login-success                          — Legacy alias for /login-bridge
//   /auth-callback                          — Legacy alias for /login-bridge
//   /dashboard                              — Authenticated internal dashboard
//                                             (protected — see <ProtectedRoute />).
//   /dashboard/risk-assessment              — Risk Intelligence Suite portal
//                                             (PROTECTED). Card on /dashboard.
//   /dashboard/risk-assessment/test         — Risk-assessment questionnaire +
//                                             results dashboard (PROTECTED).
//   /forge-ultra-dashboard                  — Legacy alias → /dashboard.
//   /risk-assessment                        — Legacy alias → /dashboard/risk-assessment
//                                             so any pre-existing WP / email link
//                                             still lands on the new suite portal.
//
// The auth gate is implemented at the routing layer here by wrapping
// each protected element in <ProtectedRoute />. Page components are
// pure UI — they trust the route guard to keep unauthorised callers
// out (Premium / Professional / administrator pass; everyone else is
// bounced to ki-wealth.com).

import { Navigate, Route, Routes } from 'react-router-dom';

import ProtectedRoute from './auth/route-guard';
import DarkModeToggle from './components/dark-mode-toggle';
import LandingPage from './components/landing-page';
import { AddToPortfolioModal } from './features/user-portfolios/components/AddToPortfolioModal';
import { MyPortfoliosPage } from './features/user-portfolios/pages/MyPortfoliosPage';
import { PortfolioDashboardPage } from './features/user-portfolios/pages/PortfolioDashboardPage';
import { AuthCallback } from './features/auth-callback';
import { EtfWealthBuilderPage } from './features/free-calculators/etf-wealth-builder-calculator';
import { FinancialFreedomPage } from './features/free-calculators/financial-freedom-calculator';
import { PortfolioDiversificationCheckerPage } from './features/portfolio-diversification-checker';
import { DriftAuditorPage } from './features/portfolio-drift-auditor';
import { ProDriftAuditorPage } from './features/portfolio-drift-auditor-pro';
import { DiversificationOptimizerProPage } from './features/portfolio-diversification-optimizer-pro';
import { PortfolioStressTestPage } from './features/portfolio-stress-test';
import { GoalAlignmentPage } from './features/goal-based-risk-alignment-tool';
import {
    RiskAssessmentFlow,
    RiskLandingPage,
} from './features/risk-assessment';
import { EtfRankingPage } from './features/etf-ranking';
import { CommoditiesRankingPage } from './features/commodities-ranking';
import { CryptoRankingPage } from './features/crypto-ranking';
import { FXRankingPage } from './features/fx-ranking';
import { ReitsRankingPage } from './features/reits-ranking';
import { ScreenerPage } from './features/screener';
import { UcitsEtfDashboard } from './features/ucits-etf';
import { BreakoutInsightsPage } from './features/insights';
import { StockRankingPage } from './features/stock-ranking';
import { StockInfoPage } from './features/stock-info/StockInfoPage';
import { EtfInfoPage } from './features/etf-info/EtfInfoPage';
import { ReitInfoPage } from './features/reit-info/ReitInfoPage';
import { CryptoInfoPage } from './features/crypto-info/CryptoInfoPage';
import { CommodityInfoPage } from './features/commodities-info/CommodityInfoPage';
import { FxInfoPage } from './features/fx-info/FxInfoPage';
import { TreasuryCurveDashboard } from './features/market-analysis/treasury-curve';
import { SectorRotationPage } from './features/market-analysis/sector-rotation';
import { InsiderTradingPage } from './features/insider-trading';
import { WealthChatPage } from './features/wealth-building-assistant-chat';
import { PortfolioOptimizationPage } from './features/portfolio-optimization';
import { MultiAssetOptimizerPage } from './features/portfolio-optimization-tools/multi-asset-portfolio-optimizer';
import {
    MyStrategiesPage,
    StrategyTearSheetPage,
} from './features/portfolio-optimization-tools/my-strategies';
import ComingSoon from './pages/coming-soon';
import Dashboard from './pages/forge-ultra-dashboard';
import { LoginBridge } from './pages/login-success';

// Asset-ranking suite + portfolio optimizer — routed now (so the
// dashboard's TIER 2/3 links land on a real in-app page instead of the
// catch-all landing redirect) but not yet built. Each renders the
// shared <ComingSoon /> placeholder until its module ships; swap in the
// real feature page here when ready.
const COMING_SOON_ROUTES: { path: string; title: string; description: string }[] = [
    {
        path: '/dashboard/crypto-reit-ranking',
        title: 'Crypto & REITs Ranking',
        description:
            'Digital assets and real-estate income vehicles ranked side by side.',
    },
];

function App() {
    return (
        <div className="min-h-screen flex flex-col bg-slate-200 dark:bg-slate-900 text-slate-900 dark:text-slate-100 transition-colors duration-300">
            <DarkModeToggle />
            <Routes>
                <Route path="/" element={<LandingPage />} />
                {/* Canonical path — matches the WP login_redirect / pmpro_login_redirect_url
                    filters in ki-wealth.com's functions.php. */}
                <Route path="/login-bridge" element={<LoginBridge />} />
                {/* Legacy aliases so any previously-issued redirect URLs keep working. */}
                <Route path="/login-success" element={<LoginBridge />} />
                <Route path="/auth-callback" element={<AuthCallback />} />

                {/* PUBLIC free tool — the ETF Wealth Builder Monte-Carlo
                    calculator. Deliberately NOT behind <ProtectedRoute>: it is a
                    top-of-funnel marketing experience an anonymous frontpage
                    visitor runs before signing up (the landing "Popular" card
                    links here). Its own page shell provides a Back-to-Home
                    escape + shared footer. */}
                <Route
                    path="/free-tools/etf-wealth-builder"
                    element={<EtfWealthBuilderPage />}
                />

                {/* PUBLIC free tool — the Financial Freedom Calculator. Like the
                    ETF Wealth Builder above, deliberately NOT behind
                    <ProtectedRoute>: a top-of-funnel marketing experience an
                    anonymous frontpage visitor runs before signing up (the
                    landing "Popular" card's "Calculate My Freedom Date" button
                    links here). Its own page shell provides a Back-to-Home
                    escape + shared footer. */}
                <Route
                    path="/free-tools/financial-freedom"
                    element={<FinancialFreedomPage />}
                />

                {/* PUBLIC free tool — the Portfolio Diversification Checker
                    (MCTR/PCTR, PCA Effective Bets, HRP clustering). Not behind
                    <ProtectedRoute>, like the calculators above. */}
                <Route
                    path="/free-tools/portfolio-diversification-checker"
                    element={<PortfolioDiversificationCheckerPage />}
                />

                {/* PUBLIC free tool — the Portfolio Drift & Rebalancing Alpha
                    Auditor (path-dependent Numba drift/calendar/corridor
                    simulation, variance-drain + tail-risk math). Not behind
                    <ProtectedRoute>, like the calculators above. */}
                <Route
                    path="/free-tools/portfolio-drift-auditor"
                    element={<DriftAuditorPage />}
                />

                {/* PROTECTED internal dashboard. */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                />

                {/* My Portfolio Auditor PRO — the institutional (paid-tier)
                    sibling of the free Drift Auditor. Tax-lot drag, periodic
                    cash-flow rebalancing, an 8-point corridor efficiency
                    frontier, an unlocked AI regime-switching trajectory with a
                    full trade ledger, and a live actionable execution blotter.
                    Calls POST /api/v1/portfolio-drift-auditor-pro/audit.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/portfolio-drift-auditor-pro"
                    element={
                        <ProtectedRoute>
                            <ProDriftAuditorPage />
                        </ProtectedRoute>
                    }
                />

                {/* My Portfolio Optimizer PRO — the institutional 5-pillar
                    diversification & optimization engine. Correlation/tail
                    contagion, Euler risk attribution, multi-factor OLS,
                    Cornish-Fisher tail stress and six rebalancing simulations
                    (Equal Weight, Inverse Vol, HRP, ERC, Max-Diversification).
                    Calls POST /api/v1/portfolio-diversification-optimizer-pro/analyze.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/portfolio-diversification-optimizer-pro"
                    element={
                        <ProtectedRoute>
                            <DiversificationOptimizerProPage />
                        </ProtectedRoute>
                    }
                />

                {/* Risk Intelligence Suite — feature-based routing under
                    /dashboard/* so the Layout / Sidebar shell is reused
                    transparently. Each leaf route renders its own
                    <Layout sidebar={<Sidebar />}> wrapper. */}
                <Route
                    path="/dashboard/risk-assessment"
                    element={
                        <ProtectedRoute>
                            <RiskLandingPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/dashboard/risk-assessment/test"
                    element={
                        <ProtectedRoute>
                            <RiskAssessmentFlow />
                        </ProtectedRoute>
                    }
                />

                {/* Portfolio Stress Test & Scenario Simulator — pulls the
                    user's recommended allocation from the risk-assessment
                    persisted row and lets them dial in a scenario +
                    investment + horizon to see real-world impact. */}
                <Route
                    path="/dashboard/portfolio-stress-test"
                    element={
                        <ProtectedRoute>
                            <PortfolioStressTestPage />
                        </ProtectedRoute>
                    }
                />

                {/* Goal-Based Risk Alignment Tool — Monte-Carlo simulator
                    that asks "given my recommended allocation, monthly
                    contribution and horizon, how likely am I to hit my
                    goal under each macro scenario?". Mounted under the
                    same /dashboard/* shell as the rest of the suite. */}
                <Route
                    path="/dashboard/goal-based-risk-alignment-tool"
                    element={
                        <ProtectedRoute>
                            <GoalAlignmentPage />
                        </ProtectedRoute>
                    }
                />

                {/* Global Screener — multi-dimensional filter over the whole
                    tradable universe, powered by the in-memory Polars engine
                    (POST /api/v1/global-search/screener/search). Live-updates
                    as the user tweaks the eight filter dimensions. PROTECTED
                    behind the same auth gate. */}
                <Route
                    path="/dashboard/screener"
                    element={
                        <ProtectedRoute>
                            <ScreenerPage />
                        </ProtectedRoute>
                    }
                />

                {/* UCITS Alpha Engine — European UCITS ETF module. Nightly
                    quantitative breakout ideas, an in-memory Polars screener
                    and Numba sector-overlap similarity, all served from the
                    RAM speed layer via GET/POST /api/v1/ucits-etfs/*.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/ucits-etf"
                    element={
                        <ProtectedRoute>
                            <UcitsEtfDashboard />
                        </ProtectedRoute>
                    }
                />

                {/* My Portfolios — the user's personal transaction-ledger hub.
                    Lists up to 6 portfolios (GET /api/v1/portfolios) and, per
                    card, fetches its own FIFO-analysed KPI metrics
                    (GET /api/v1/portfolios/{id}/dashboard) via decoupled
                    fetching. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/my-portfolios"
                    element={
                        <ProtectedRoute>
                            <MyPortfoliosPage />
                        </ProtectedRoute>
                    }
                />
                {/* Portfolio Analytics detail — fetches the FIFO-analysed
                    dashboard (GET /api/v1/portfolios/{id}/dashboard) and renders
                    the KPI strip, Recharts allocation donut, best/worst
                    performer widgets and the sortable TanStack ledger grid.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/my-portfolios/:id"
                    element={
                        <ProtectedRoute>
                            <PortfolioDashboardPage />
                        </ProtectedRoute>
                    }
                />

                {/* Momentum Insights — the daily actionable breakout dashboard
                    (Knowledge → Insights in the sidebar). Renders the Top 10
                    Buy and Top 10 Sell breakout setups from
                    GET /api/v1/breakout-alerts/insights/top, served entirely
                    off the composite DB index (no live FMP / Numba on the
                    request path). PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/insights"
                    element={
                        <ProtectedRoute>
                            <BreakoutInsightsPage />
                        </ProtectedRoute>
                    }
                />
                {/* Legacy alias: the sidebar historically linked to /insights.
                    Redirect so any old bookmark lands on the real page. */}
                <Route
                    path="/insights"
                    element={<Navigate to="/dashboard/insights" replace />}
                />

                {/* Equities Ranking — the first live module of the asset-
                    ranking suite. Fetches the daily Forge Ultra leaderboard
                    from GET /api/v1/ranking/top-assets and renders the shared
                    <RankingTable>. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/stock-ranking"
                    element={
                        <ProtectedRoute>
                            <StockRankingPage />
                        </ProtectedRoute>
                    }
                />

                {/* Stock Info Deep Dive — company-level institutional analysis.
                    Fetches live quote, financials, targets, grades, and peers
                    from GET /api/v1/stock-info/* using Component Islands for
                    progressive loading. Accessed via ?symbol= query param from
                    the Stock Ranking popup. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/stock-info-page"
                    element={
                        <ProtectedRoute>
                            <StockInfoPage />
                        </ProtectedRoute>
                    }
                />

                {/* ETF Info Deep Dive — fund-level institutional analysis.
                    Fetches live quote, static ETF info (AUM/expense/NAV),
                    top holdings, sector/country allocations, and peer ETFs
                    from GET /api/v1/etf-info/* using Component Islands for
                    progressive loading. Accessed via ?symbol= query param.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/etf-info-page"
                    element={
                        <ProtectedRoute>
                            <EtfInfoPage />
                        </ProtectedRoute>
                    }
                />

                {/* REIT Info Deep Dive — Real-Estate-level institutional analysis.
                    Fetches live quote, TTM yield ratios (dividend yield, payout,
                    Price/FFO proxy), dividend-history track record, leverage &
                    safety metrics, and peer REITs from GET /api/v1/reit-info/*
                    using Component Islands for progressive loading. Accessed via
                    ?symbol= query param from the REITs Ranking popup. PROTECTED
                    behind the same auth gate. */}
                <Route
                    path="/dashboard/reit-info-page"
                    element={
                        <ProtectedRoute>
                            <ReitInfoPage />
                        </ProtectedRoute>
                    }
                />

                {/* Crypto Info Deep Dive — digital-asset institutional analysis.
                    Fetches the live quote, static description, and a
                    server-computed swing-trading breakout alert from
                    GET /api/v1/crypto-info/* using Component Islands for
                    progressive loading. Accessed via ?symbol= query param from
                    the Crypto Ranking popup. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/crypto-info-page"
                    element={
                        <ProtectedRoute>
                            <CryptoInfoPage />
                        </ProtectedRoute>
                    }
                />

                {/* Commodity Info Deep Dive — static macro metadata (description
                    + strategic playbook) from GET /api/v1/commodities-info/*,
                    plus two quantitative islands (Forge Ultra Signal trend + COT
                    net positioning) reusing the commodities-ranking endpoints.
                    Accessed via ?symbol= query param from the Commodities
                    Ranking table. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/commodity-info-page"
                    element={
                        <ProtectedRoute>
                            <CommodityInfoPage />
                        </ProtectedRoute>
                    }
                />

                {/* FX Info Deep Dive — single-currency institutional Tear Sheet.
                    Fetches pre-computed AFML fractional differencing, realized
                    volatility cones, calendar seasonality and look-ahead-safe COT
                    crowding z-scores from GET /api/v1/fx-info/* (backend un-inverts
                    USD-base pairs back to Market Convention). Accessed via the
                    "More Info…" action in the FX Ranking detail popup using a
                    ?symbol= query param. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/fx-info-page"
                    element={
                        <ProtectedRoute>
                            <FxInfoPage />
                        </ProtectedRoute>
                    }
                />

                {/* Fixed-Income / ETF Ranking — bond-universe sibling of the
                    equities module. Fetches the daily Forge ETF leaderboard
                    from GET /api/v1/etf-ranking/* and renders the category
                    radar, risk/return scatter and sortable universe table.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/etf-ranking"
                    element={
                        <ProtectedRoute>
                            <EtfRankingPage />
                        </ProtectedRoute>
                    }
                />

                {/* Commodities Ranking — futures-complex sibling of the
                    equities/ETF modules. Fetches the daily Forge commodities
                    leaderboard from GET /api/v1/commodities-ranking/* and
                    renders the category radar, risk/return scatter, sortable
                    universe table and per-commodity COT positioning pop-over.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/commodities-ranking"
                    element={
                        <ProtectedRoute>
                            <CommoditiesRankingPage />
                        </ProtectedRoute>
                    }
                />

                {/* REITs Ranking — Real-Estate equities sibling of the
                    equities/ETF modules. Fetches the daily Forge Ultra REIT
                    leaderboard from GET /api/v1/reits-ranking/* and renders
                    the factor summary, risk/return scatter and sortable
                    universe table. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/reits-ranking"
                    element={
                        <ProtectedRoute>
                            <ReitsRankingPage />
                        </ProtectedRoute>
                    }
                />

                {/* Crypto Ranking — digital-asset sibling of the
                    equities/ETF modules. Fetches the daily Forge Ultra
                    crypto leaderboard from GET /api/v1/crypto-ranking/* and
                    renders the top-coin factor radar, risk/return scatter
                    and sortable universe table. PROTECTED behind the same
                    auth gate. */}
                <Route
                    path="/dashboard/crypto-ranking"
                    element={
                        <ProtectedRoute>
                            <CryptoRankingPage />
                        </ProtectedRoute>
                    }
                />

                {/* FX Ranking — institutional 30-currency FX sibling of the
                    equities/ETF/commodities modules. Fetches the daily Forge
                    FX leaderboard from GET /api/v1/fx-ranking/* and renders
                    the tier radar, risk/return scatter, sortable universe
                    table and per-currency COT positioning pop-over.
                    PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/fx-ranking"
                    element={
                        <ProtectedRoute>
                            <FXRankingPage />
                        </ProtectedRoute>
                    }
                />

                {/* Market Analysis · Treasury Yield Curve — institutional
                    fixed-income analytics (spreads, curve regimes, momentum
                    and rolling volatility). Fetches the CQRS read endpoints
                    GET /api/v1/market-analysis/treasury-curve/* via React
                    Query. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/market-analysis"
                    element={
                        <ProtectedRoute>
                            <TreasuryCurveDashboard />
                        </ProtectedRoute>
                    }
                />

                {/* Market Analysis · Sector Rotation — institutional
                    relative-strength analytics (JdK Relative Rotation Graphs,
                    macro-cycle sector tilts fused with the AFML transition
                    signal, and SciPy Ward-clustered correlation matrices).
                    Fetches the CQRS read endpoints
                    GET /api/v1/market-analysis/sector-rotation/* via React
                    Query. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/market-analysis/sector-rotation"
                    element={
                        <ProtectedRoute>
                            <SectorRotationPage />
                        </ProtectedRoute>
                    }
                />

                {/* Insider Trading Radar — Top-20 most liquid names with SEC
                    Form-4 open-market insider Purchases / Sales, served from
                    GET /api/v1/insider-trading/top-liquid. PROTECTED. */}
                <Route
                    path="/dashboard/insider-trading"
                    element={
                        <ProtectedRoute>
                            <InsiderTradingPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/insider-trading"
                    element={<Navigate to="/dashboard/insider-trading" replace />}
                />

                {/* Wealth-Building Assistant — streaming LLM education chat,
                    POST /api/wealth-chat/stream (JWT-gated). PROTECTED. */}
                <Route
                    path="/dashboard/wealth-assistant"
                    element={
                        <ProtectedRoute>
                            <WealthChatPage />
                        </ProtectedRoute>
                    }
                />

                {/* Portfolio Optimizer — the capstone allocation module.
                    Fetches the full Black-Litterman target-weight matrix
                    (all 5 risk bands) from GET /api/v1/portfolio/matrix and
                    renders the pre-fetch-and-slide risk slider, sector donut
                    and glass-box holdings table. PROTECTED behind the same
                    auth gate. */}
                <Route
                    path="/dashboard/stock-only-portfolio"
                    element={
                        <ProtectedRoute>
                            <PortfolioOptimizationPage />
                        </ProtectedRoute>
                    }
                />

                {/* Multi-Asset Portfolio Optimizer — interactive "What-If"
                    cockpit for the 3-tier cross-asset engine. Reads the
                    optimiser defaults / sleeve universe and the dynamic
                    Tier-3 risk-aversion delta live from
                    /api/v1/multi-asset-portfolio/*; runs the builder and can
                    publish an approved book. PROTECTED behind the same auth
                    gate. */}
                <Route
                    path="/dashboard/multi-asset-portfolio-optimizer"
                    element={
                        <ProtectedRoute>
                            <MultiAssetOptimizerPage />
                        </ProtectedRoute>
                    }
                />

                {/* My Strategies — the saved-portfolios master hub. Lists every
                    persisted book (GET /api/v1/reports), plots the efficient
                    frontier (Risk vs. Reward), and manages the nightly
                    live-tracking daemon. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/my-strategies"
                    element={
                        <ProtectedRoute>
                            <MyStrategiesPage />
                        </ProtectedRoute>
                    }
                />
                {/* Strategy detail (Tear Sheet) — the post-save redirect target.
                    Hydrates a saved report and dynamically computes the relative
                    alpha (Information Ratio, Hit Rate, Drawdown Defense) vs. a
                    chosen benchmark, rendering the comparative outperformance
                    chart. PROTECTED behind the same auth gate. */}
                <Route
                    path="/dashboard/my-strategies/:reportId"
                    element={
                        <ProtectedRoute>
                            <StrategyTearSheetPage />
                        </ProtectedRoute>
                    }
                />

                {/* Asset-ranking suite + portfolio optimizer — placeholder
                    routes rendering <ComingSoon /> until each module is built.
                    PROTECTED so they stay behind the same auth gate as the
                    rest of the dashboard. Swap the element for the real
                    feature page when the module ships. */}
                {COMING_SOON_ROUTES.map((route) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={
                            <ProtectedRoute>
                                <ComingSoon
                                    title={route.title}
                                    description={route.description}
                                />
                            </ProtectedRoute>
                        }
                    />
                ))}

                {/* Legacy URL kept alive so old WP redirects + bookmarks still land. */}
                <Route
                    path="/forge-ultra-dashboard/*"
                    element={<Navigate to="/dashboard" replace />}
                />
                {/* Pre-feature-routing alias: the suite used to be served at
                    /risk-assessment. Redirect rather than 404 so any email
                    or marketing link survives the rebuild. */}
                <Route
                    path="/risk-assessment"
                    element={<Navigate to="/dashboard/risk-assessment" replace />}
                />
                {/* Catch-all: anything unknown falls back to the landing page. */}
                <Route path="*" element={<LandingPage />} />
            </Routes>

            {/* Global "Add to Portfolio" overlay — mounted once, driven by the
                Zustand store. Invisible (and effectively free) until an Info
                Page calls openModal(). Kept OUTSIDE <Routes> so it persists
                across navigation. */}
            <AddToPortfolioModal />
        </div>
    );
}

export default App;